/**
 * The sequencer (05 §3): plays a compiled song on the SID voices, scheduled ahead on the
 * WebAudio clock (clock.ts). One song at a time; changing song fades the old one out under the
 * new one's start.
 */

import type { Bus } from '../bus';
import type { Sid } from '../sid';
import { collectDue, type Cursor, LOOKAHEAD, onceEnds, stepSeconds } from './clock';
import { voicesFor } from './instruments';
import type { Shift } from './songs';
import type { Compiled } from './types';

const FADE_IN = 0.04;
const FADE_OUT = 0.9;
/** Step 0 lands this long after `play`, so the first notes are not already late. */
const LEAD_IN = 0.06;

interface Run {
  readonly name: string;
  readonly song: Compiled;
  readonly transpose: number;
  readonly out: GainNode;
  readonly start: number;
  readonly stepSec: number;
  readonly cursor: Cursor;
}

export class MusicPlayer {
  private run: Run | null = null;
  /** Songs fading out, and when to let go of them. */
  private fading: { out: GainNode; until: number }[] = [];

  constructor(
    private readonly bus: Bus,
    private readonly sid: Sid,
  ) {}

  /** The song playing, or null (none, or a once-only song has finished). */
  get playing(): string | null {
    return this.run?.name ?? null;
  }

  /** Start `song` from the top under `name`, fading out whatever was playing. Same name: nothing changes. */
  play(name: string, song: Compiled, shift: Shift): void {
    if (this.run?.name === name) return;
    this.fadeOut(FADE_OUT);
    const now = this.bus.now;
    const out = this.bus.ctx.createGain();
    out.gain.setValueAtTime(0, now);
    out.gain.linearRampToValueAtTime(1, now + FADE_IN);
    out.connect(this.bus.music);
    this.run = {
      name,
      song,
      transpose: shift.transpose,
      out,
      start: now + LEAD_IN,
      stepSec: stepSeconds(song.bpm * shift.tempo),
      cursor: { index: 0, pass: 0 },
    };
    this.tick();
  }

  stop(): void {
    this.fadeOut(FADE_OUT * 0.6);
  }

  /** Hand WebAudio every note due in the look-ahead window. Call every `TICK_MS`. */
  tick(): void {
    const now = this.bus.now;
    this.fading = this.fading.filter((f) => {
      if (now < f.until) return true;
      f.out.disconnect();
      return false;
    });
    const r = this.run;
    if (!r) return;
    for (const due of collectDue(r.song, r.cursor, r.start, r.stepSec, now, now + LOOKAHEAD)) {
      for (const v of voicesFor(due.event, r.stepSec, r.song.mix, r.transpose)) this.sid.play(v, due.time, r.out);
    }
    if (!r.song.loop && now > onceEnds(r.song, r.start, r.stepSec)) {
      r.out.disconnect();
      this.run = null;
    }
  }

  private fadeOut(seconds: number): void {
    const r = this.run;
    if (!r) return;
    this.run = null;
    const g = r.out.gain;
    const now = this.bus.now;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(0, now + seconds);
    this.fading.push({ out: r.out, until: now + seconds + 0.1 });
  }
}
